import { useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import {
  UniversalFilterProjectStatusStatus,
  UniversalFilterProjectStatusValue,
  UniversalFilterProjectStatusModes,
  UniversalFilterProjectStatusFilterState,
  UniversalFilterProjectStatusOnChange
} from '../../UniversalFilterProjectStatus.types';

interface UniversalFilterProjectStatusFilterStateOptions {
  onChange: UniversalFilterProjectStatusOnChange;
}

function useUniversalFilterProjectStatusFilterState({
  onChange
}: UniversalFilterProjectStatusFilterStateOptions) {
  // All modes and options available in handleApplyFilterState
  // must be implemented in handleParseFilterValue too
  const handleApplyFilterState = useCallback<
    (filterState: UniversalFilterProjectStatusFilterState) => void
  >(
    (filterState) => {
      const statuses = filterState?.statuses;

      if (filterState?.mode === UniversalFilterProjectStatusModes.IS_ANY_OF) {
        onChange(isEmpty(statuses) ? undefined : { in: statuses });
        return;
      }

      if (filterState?.mode === UniversalFilterProjectStatusModes.IS_NONE_OF) {
        onChange(isEmpty(statuses) ? undefined : { notIn: statuses });
        return;
      }

      onChange();
    },
    [onChange]
  );

  const handleParseFilterValue = useCallback<
    (
      filterValue: UniversalFilterProjectStatusValue
    ) => UniversalFilterProjectStatusFilterState | undefined
  >((filterValue) => {
    if (isNil(filterValue)) {
      return undefined;
    }

    if (!isEmpty(filterValue.in)) {
      return {
        mode: UniversalFilterProjectStatusModes.IS_ANY_OF,
        statuses: filterValue.in as UniversalFilterProjectStatusStatus[]
      };
    }

    if (!isEmpty(filterValue.notIn)) {
      return {
        mode: UniversalFilterProjectStatusModes.IS_NONE_OF,
        statuses: filterValue.notIn as UniversalFilterProjectStatusStatus[]
      };
    }

    return undefined;
  }, []);

  return { handleApplyFilterState, handleParseFilterValue };
}

export default useUniversalFilterProjectStatusFilterState;
